import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Bolt as FlashIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Home as HomeIcon,
  LiveTv as LiveIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PracticePointsLeaderboard from './PracticePointsLeaderboard';
import { formatPoints } from './flashPracticeUtils';

const ResultsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '16px',
  marginBottom: '24px',
}));

const FlashQuizResults = ({ totalScore, position, leaderboard, userAnswers, onBack, onPlayAgain, practiceMode = false, onPlayLive }) => {
  const getRankColor = (pos) => {
    if (pos === 1) return '#FFD700'; // Gold
    if (pos === 2) return '#C0C0C0'; // Silver
    if (pos === 3) return '#CD7F32'; // Bronze
    return '#9C27B0';
  };

  // Reserved for future: rank badge logic if needed

  // Aggregations are not displayed in the simplified UI; keeping answers below

  return (
    <ResultsContainer>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <FlashIcon sx={{ fontSize: 80, color: '#9C27B0', mb: 2 }} />
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
            {practiceMode ? 'Demo Complete!' : 'Flash Quiz Complete!'}
          </Typography>
          <Typography variant="h6" sx={{ color: '#ccc', mb: practiceMode ? 2 : 0 }}>
            {practiceMode
              ? 'Nice work! You crushed the demo round.'
              : 'Great job! Here are your results'}
          </Typography>
          {practiceMode && (
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 4,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(156,39,176,0.2) 0%, rgba(15,23,42,0.8) 100%)',
                border: '1px solid rgba(168,85,247,0.4)',
              }}
            >
              <Typography variant="overline" sx={{ color: '#c4b5fd', letterSpacing: 2, fontWeight: 700 }}>
                YOUR FINAL SCORE
              </Typography>
              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800 }}>
                {formatPoints(totalScore)}
                <Typography component="span" variant="h6" sx={{ color: '#a78bfa', ml: 1 }}>pts</Typography>
              </Typography>
              {position ? (
                <Typography variant="body1" sx={{ color: '#fde047', fontWeight: 600, mt: 0.5 }}>
                  You finished #{position} on the demo board
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>

          {/* Leaderboard */}
          <Grid item xs={12}>
            {practiceMode ? (
              <PracticePointsLeaderboard
                leaderboard={leaderboard}
                title="Final demo leaderboard"
              />
            ) : (
            <StyledCard>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <TrophyIcon sx={{ mr: 1, color: '#FFD700' }} />
                  Final Leaderboard
                </Typography>
                <Divider sx={{ borderColor: '#333', mb: 2 }} />

                <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {leaderboard.slice(0, 10).map((entry, index) => {
                    const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;

                    return (
                      <ListItem
                        key={entry.participant_id}
                        sx={{
                          bgcolor: entry.current_position === position ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                          borderRadius: '8px',
                          mb: 1,
                          border: entry.current_position === position ? '1px solid #9C27B0' : 'none'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: getRankColor(entry.current_position) }}>
                            {medal || entry.current_position}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography sx={{ color: '#fff', fontWeight: entry.current_position === position ? 'bold' : 'normal' }}>
                              {entry.fname && entry.lname
                                ? `${entry.fname} ${entry.lname}`
                                : entry.guest_name || 'Guest'}
                              {entry.current_position === position && ' (You)'}
                            </Typography>
                          }
                        // secondary={
                        //   <Typography variant="body2" sx={{ color: '#ccc' }}>
                        //     {entry.percentage}% accuracy
                        //   </Typography>
                        // }
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" sx={{ color: '#9C27B0', fontWeight: 'bold' }}>
                            {entry.current_score}
                          </Typography>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </StyledCard>
            )}
          </Grid>

          {/* Primary Action Card */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {practiceMode && onPlayLive && (
                <Button
                  variant="contained"
                  startIcon={<LiveIcon />}
                  onClick={onPlayLive}
                  sx={{
                    bgcolor: '#00c853',
                    '&:hover': { bgcolor: '#00a844' },
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: '10px',
                    boxShadow: '0 10px 30px rgba(0,200,83,0.25)',
                  }}
                >
                  Play Live
                </Button>
              )}
              <Button
                variant={practiceMode ? 'outlined' : 'contained'}
                startIcon={<HomeIcon />}
                onClick={onBack}
                sx={{
                  bgcolor: practiceMode ? 'transparent' : '#9C27B0',
                  borderColor: practiceMode ? '#9C27B0' : undefined,
                  color: practiceMode ? '#9C27B0' : '#fff',
                  '&:hover': {
                    bgcolor: practiceMode ? 'rgba(156, 39, 176, 0.1)' : '#7B1FA2',
                    borderColor: '#7B1FA2',
                  },
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: '10px',
                  boxShadow: practiceMode ? 'none' : '0 10px 30px rgba(156,39,176,0.25)',
                }}
              >
                Back to Home
              </Button>
            </Box>
          </Grid>


          {/* Answer Summary - Full width */}
          {userAnswers.length > 0 && (
            <Grid item xs={12}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ mr: 1, color: '#FFD700' }} />
                    Your Answers
                  </Typography>
                  <Divider sx={{ borderColor: '#333', mb: 2 }} />

                  <Grid container spacing={2}>
                    {userAnswers.map((answer, idx) => (
                      <Grid item xs={12} key={idx}>
                        <Box sx={{ p: 2, bgcolor: '#121212', borderRadius: '8px', border: '1px solid #333' }}>
                          <Typography variant="subtitle1" sx={{ color: '#9C27B0', fontWeight: 'bold', mb: 1 }}>
                            {answer.song?.name || 'Unknown Song'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
                            {answer.song?.artist || 'Unknown Artist'}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {answer.results?.answers?.map((ans, i) => (
                              <Chip
                                key={i}
                                icon={ans.isCorrect ? <CheckIcon /> : <CancelIcon />}
                                label={`Q${i + 1}: ${ans.points} pts`}
                                sx={{
                                  bgcolor: ans.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                  color: ans.isCorrect ? '#4CAF50' : '#f44336',
                                  border: `1px solid ${ans.isCorrect ? '#4CAF50' : '#f44336'}`
                                }}
                              />
                            ))}
                          </Box>

                          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mt: 2 }}>
                            Score: {answer.results?.totalScore || 0}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          )}
        </Grid>
      </Container>
    </ResultsContainer>
  );
};

export default FlashQuizResults;


